import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { VaultKeTheme } from '../utils/theme';

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const { scrollY } = useScroll();
  const backgroundOpacity = useTransform(scrollY, [0, 100], [0.8, 0.95]);
  const blurAmount = useTransform(scrollY, [0, 100], [10, 20]);

  const styles: { [key: string]: React.CSSProperties } = {
    nav: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      backgroundColor: VaultKeTheme.colors.background,
      borderBottom: `1px solid ${VaultKeTheme.colors.border}`,
      padding: `${VaultKeTheme.spacing.md} ${VaultKeTheme.spacing.xl}`,
      zIndex: 1000,
      backdropFilter: 'blur(10px)',
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    logo: {
      ...VaultKeTheme.typography.h4,
      color: VaultKeTheme.colors.primary,
      fontWeight: 'bold',
      textDecoration: 'none',
      margin: 0,
    },
    menuButton: {
      display: isMobile ? 'block' : 'none',
      background: 'none',
      border: 'none',
      color: VaultKeTheme.colors.text,
      fontSize: '1.5rem',
      cursor: 'pointer',
    },
    navLinks: {
      display: isMobile ? 'none' : 'flex',
      alignItems: 'center',
      gap: VaultKeTheme.spacing.xl,
    },
    link: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      textDecoration: 'none',
      transition: 'color 0.2s ease',
      cursor: 'pointer',
      padding: '8px 12px',
      borderRadius: '4px',
    },
    activeLink: {
      backgroundColor: VaultKeTheme.colors.primary,
      color: VaultKeTheme.colors.background,
    },
    button: {
      ...VaultKeTheme.components.button.secondary,
      padding: '8px 16px',
      fontSize: '0.9rem',
    },
    mobileMenu: {
      display: 'none',
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: VaultKeTheme.colors.surface,
      borderBottom: `1px solid ${VaultKeTheme.colors.border}`,
      padding: VaultKeTheme.spacing.lg,
    },
  };


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      // Close mobile menu when scrolling
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <motion.nav
      style={{
        ...styles.nav,
        backgroundColor: isScrolled
          ? `rgba(13, 17, 23, ${backgroundOpacity})`
          : VaultKeTheme.colors.background,
        backdropFilter: `blur(${blurAmount}px)`,
      }}
      animate={{
        boxShadow: isScrolled
          ? '0 4px 20px rgba(0, 0, 0, 0.15)'
          : 'none',
      }}
      transition={{ duration: 0.3 }}
    >
      <div style={styles.container}>
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Link to="/" style={styles.logo}>
            VaultKe
          </Link>
        </motion.div>

        <button
          style={styles.menuButton}
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <motion.span
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            animate={{ rotate: isMenuOpen ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {isMenuOpen ? '✕' : '☰'}
          </motion.span>
        </button>

        <div style={styles.navLinks}>
          <Link
            to="/"
            style={{
              ...styles.link,
              ...(location.pathname === '/' ? styles.activeLink : {}),
            }}
          >
            Home
          </Link>
          <Link
            to="/pricing"
            style={{
              ...styles.link,
              ...(location.pathname === '/pricing' ? styles.activeLink : {}),
            }}
          >
            Pricing
          </Link>
          <Link
            to="/about"
            style={{
              ...styles.link,
              ...(location.pathname === '/about' ? styles.activeLink : {}),
            }}
          >
            About
          </Link>
          <Link
            to="/contact"
            style={{
              ...styles.link,
              ...(location.pathname === '/contact' ? styles.activeLink : {}),
            }}
          >
            Contact
          </Link>
          <Link to="/login" style={styles.button}>Sign In</Link>
        </div>
      </div>

      <motion.div
        style={{
          ...styles.mobileMenu,
          display: isMenuOpen ? 'block' : 'none',
        }}
        initial={{ opacity: 0, y: -20 }}
        animate={{
          opacity: isMenuOpen ? 1 : 0,
          y: isMenuOpen ? 0 : -20,
        }}
        transition={{ duration: 0.3 }}
      >
        <Link
          to="/pricing"
          style={{
            ...styles.link,
            ...(location.pathname === '/pricing' ? styles.activeLink : {}),
          }}
        >
          Pricing
        </Link>
        <Link
          to="/about"
          style={{
            ...styles.link,
            ...(location.pathname === '/about' ? styles.activeLink : {}),
          }}
        >
          About
        </Link>
        <Link
          to="/contact"
          style={{
            ...styles.link,
            ...(location.pathname === '/contact' ? styles.activeLink : {}),
          }}
        >
          Contact
        </Link>
        <Link to="/login" style={styles.button}>Sign In</Link>
      </motion.div>
    </motion.nav>
  );
};

export default Navigation;